/**
 * Partner billing in the Admin (W3-PARTNER-BILLING, D368, ADR-0048) — framework-neutral view-models for the Finance ›
 * Partner billing screen: the accounts' open exposure (the revolving credit line, D371 #1), statement statuses, the
 * early-close and reject reason vocabularies, and the four-eyes rule as the console shows it (D371 #3).
 *
 * The console only FILTERS: it hides the approve/reject buttons from the operator who recorded a payment (the maker),
 * but the authority is the server, which compares the SESSION identities and answers `403 SELF_APPROVAL` whatever the
 * console shows. Money stays in integer minor units; the ratio is display-only and computed in bigint.
 */
import {
  PARTNER_CREDIT_NOTE_REASONS,
  PARTNER_EARLY_CLOSE_REASONS,
  PARTNER_PAYMENT_REJECT_REASONS,
  type PartnerExposure,
  type PartnerStatement,
  type PartnerStatementPayment,
} from '@vello/api-client';

export type PartnerBillingTone = 'neutral' | 'success' | 'warning' | 'danger';

export const EARLY_CLOSE_REASON_CODES: readonly string[] = PARTNER_EARLY_CLOSE_REASONS;
export const PAYMENT_REJECT_REASON_CODES: readonly string[] = PARTNER_PAYMENT_REJECT_REASONS;
/** Why a partner is credited (D398) — the credit-note form's reason select. */
export const CREDIT_NOTE_REASON_CODES: readonly string[] = PARTNER_CREDIT_NOTE_REASONS;

export function partnerStatementTone(status: PartnerStatement['status']): PartnerBillingTone {
  return status === 'PAID' ? 'success' : status === 'CLOSED' ? 'warning' : 'neutral';
}

export function partnerPaymentTone(status: PartnerStatementPayment['status']): PartnerBillingTone {
  return status === 'APPROVED' ? 'success' : status === 'REJECTED' ? 'danger' : 'warning';
}

/** Committed / limit in thousandths (0..1000), from minor units; undefined when unreadable or uncapped. */
export function exposurePermille(exposure: PartnerExposure | null | undefined): number | undefined {
  if (exposure === null || exposure === undefined) return undefined;
  const limit = /^\d+$/.test(exposure.limit.amountMinor) ? BigInt(exposure.limit.amountMinor) : undefined;
  const committed = /^\d+$/.test(exposure.committed.amountMinor) ? BigInt(exposure.committed.amountMinor) : undefined;
  if (limit === undefined || committed === undefined || exposure.limit.currency !== exposure.committed.currency) return undefined;
  if (limit === 0n) return committed > 0n ? 1000 : 0;
  const ratio = (committed * 1000n) / limit;
  return Number(ratio > 1000n ? 1000n : ratio);
}

/**
 * Whether THIS operator may decide a payment in the console: it must be pending and recorded by someone else. The
 * server enforces the same rule on the session identity (the console never relies on it).
 */
export function canDecidePayment(payment: PartnerStatementPayment, operatorId: string | undefined, selfApproval = false): boolean {
  // D868: a GLOBAL super administrator decides the payment they recorded too (an unattributed one stays undecidable).
  return payment.status === 'PENDING_APPROVAL' && operatorId !== undefined && payment.makerId !== undefined && (payment.makerId !== operatorId || selfApproval);
}

/** A statement's payment may be recorded only once it is CLOSED and no payment waits for approval. */
export function canRecordPayment(statement: PartnerStatement, payments: readonly PartnerStatementPayment[]): boolean {
  return statement.status === 'CLOSED' && !payments.some((p) => p.status === 'PENDING_APPROVAL' || p.status === 'APPROVED');
}

/** Today's calendar date (`YYYY-MM-DD`) in UTC, the default "paid on" of a new payment. */
export function todayIsoDate(nowMs: number): string {
  return new Date(nowMs).toISOString().slice(0, 10);
}

/**
 * What the payment of a statement must be (D398): its AMOUNT DUE — the frozen total less its credit notes — as the server
 * answers it; the total for a server that predates credit notes. Null while OPEN.
 */
export function statementAmountDue(statement: PartnerStatement): PartnerStatement['total'] {
  return statement.amountDue ?? statement.total;
}

/**
 * A credit note may be proposed on a CLOSED statement while no payment waits for approval or settled it (D398). Display
 * only — the server re-decides (market subtotal, amount due stays positive) and routes it through the refund four eyes.
 */
export function canIssueCreditNote(statement: PartnerStatement, payments: readonly PartnerStatementPayment[]): boolean {
  return statement.status === 'CLOSED' && !payments.some((p) => p.status === 'PENDING_APPROVAL' || p.status === 'APPROVED');
}
